import {
  Input,
  Textarea,
  Select,
  RadioGroup,
  Stack,
  Radio,
  SimpleGrid,
  Spinner,
  Center,
  FormControl,
  FormLabel,
  FormErrorMessage,
} from "@chakra-ui/react";
import LocationAutocomplete from "components/elements/LocationAutocomplete";
import { MoneyInput } from "components/elements/MoneyInput";
import { PercentInput } from "components/elements/PercentField";
import { FieldValue } from "utils/types";
import { useContext, useEffect } from "react";
import { useTenant } from "modules/tenant/providers/tenant-provider";
import FloatingInterestRate from "components/elements/FloatingInterestRate";
import MaskInputs from "components/elements/PatternInputs";
import { LoanContext } from "providers/loan";
import { apiRequest } from "utils/api";
import { AlertsContext } from "providers/alerts";
import { getErrorMessage } from "utils/errorHandler";
import { DateInput } from "components/elements/DateInput/date-input";
import { InputEncrypted } from "components/elements/InputEncrypted/InputEncrypted";
import { LoanFieldModel } from "providers/loans/loan-field.model";
import { PropertySelect } from "../CreateNewLoanApplication/components/PropertySelect";
import {
  PropertyForTheCompanyModel,
  usePropertiesForTheCompany,
} from "hooks/usePropertiesForTheCompany";
import { useQuery } from "@tanstack/react-query";

const LoanTypeFields = ({
  fields,
  values,
  setValue,
  columns = 2,
  missingFieldIds,
  calculateTLA,
}: {
  fields: LoanFieldModel[];
  values: any;
  setValue: (id: string, value: FieldValue) => void;
  columns?: number;
  missingFieldIds: string[];
  calculateTLA: (value: Object) => void;
}) => {
  const { user } = useTenant();
  const { loan } = useContext(LoanContext);
  const { showError } = useContext(AlertsContext);

  const { data: bcontacts = [] } = useQuery({
    queryKey: ["borrowerContacts"],
    queryFn: async () => {
      try {
        const { users } = await apiRequest("/supabase/borrowers/select");
        return (
          users
            ?.filter((user: any) => user.is_developer !== true)
            .map((borrower: any) => ({
              label: `${borrower.first_name} ${borrower.last_name}`,
              value: borrower.id as string,
              company: borrower.company.name,
              search: `${borrower.first_name} ${borrower.last_name} ${borrower.company.name}`,
            })) || []
        );
      } catch (err) {
        showError({
          title: "Error showing borrower contacts dropdown",
          description: getErrorMessage(err),
        });
        return [];
      }
    },
  });

  const { data: properties } = usePropertiesForTheCompany(loan?.borrower_company.id);

  const getProperty = (field: LoanFieldModel): PropertyForTheCompanyModel | null => {
    let propertyId: number | undefined | null = null;

    const value = values[field.id];

    if (value) {
      if (typeof value === "string") {
        propertyId = Number(value);
      } else if (typeof value === "number") {
        propertyId = value;
      } else if (typeof value === "object" && value.id) {
        propertyId = value.id;
        setValue(field.id, propertyId);
      }
    } else {
      propertyId = loan?.property?.id;
    }

    if (propertyId) {
      return properties?.find(p => p.id === propertyId) ?? null;
    }

    return null;
  };

  const handlePropertyChange = (
    field: LoanFieldModel,
    property: PropertyForTheCompanyModel | null
  ) => {
    if (property) {
      setValue(field.id, property.id);
    } else {
      setValue(field.id, null);
    }
  };

  const formatPhoneNumber = (value: string) => {
    if (!value) return value;
    const phoneNumber = value.replace(/[^\d]/g, "");
    const phoneNumberLength = phoneNumber.length;
    if (phoneNumberLength < 4) return phoneNumber;
    if (phoneNumberLength < 7) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    }
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
  };

  useEffect(() => {
    // loadBorrowerContacts();
  }, []);

  if (fields.length === 0) {
    return (
      <Center>
        <Spinner />
      </Center>
    );
  }

  calculateTLA(values);

  function isFieldDisabled(userType: any, field: any) {
    if (!field.editable) {
      return true;
    }

    if (userType === "borrower" && field.do_not_allow_borrower_to_edit) {
      return true;
    }

    if (field.section && field.section.disable_fields) {
      return true;
    }

    return false;
  }

  const handleLocationChange = (locationData: any, fieldId: string) => {
    setValue(fieldId, locationData.formatted);

    const currentField = fields.find(field => field.id === fieldId);

    if (currentField && currentField.identifier_key === "address") {
      fields.forEach(field => {
        if (field.identifier_key === "city") {
          setValue(field.id, locationData.city);
        } else if (field.identifier_key === "state") {
          setValue(field.id, locationData.state);
        } else if (field.identifier_key === "zip_code") {
          setValue(field.id, locationData.zip);
        } else if (field.identifier_key === "county") {
          setValue(field.id, locationData.county);
        }
      });
    }
  };

  const handleSelectChange = (field: LoanFieldModel, value: string) => {
    setValue(field.id, value);
  };

  const isValidEmail = (email: string) => {
    if (!email) {
      return null;
    }
    const re =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
  };

  function getValue(field: any) {
    return values[field.id] ?? null;
  }

  const getPlaceholderForInputText = (field: any) => {
    return field.placeholder?.trim() !== "" ? field.placeholder : field.label;
  };

  return (
    <SimpleGrid columns={columns} spacingX={4} spacingY={4}>
      {fields
        .sort((a, b) => a.order - b.order)
        .map(field => (
          <FormControl
            key={field.id}
            isRequired={field.required}
            isDisabled={isFieldDisabled(user?.type, field)}
            isInvalid={missingFieldIds.includes(field.id)}
          >
            <FormLabel fontSize="14px" fontWeight="500" mb="0px">
              {field.label}
            </FormLabel>

            {field.type === "text" && (
              <Input
                type="text"
                data-cy={field.label}
                value={(values[field.id] as string) || ""}
                onChange={ev => setValue(field.id, ev.target.value)}
                placeholder={getPlaceholderForInputText(field)}
                variant="primary"
              />
            )}

            {field.type === "encrypted" && (
              <InputEncrypted
                fieldId={field.id}
                label={field.label}
                setValue={setValue}
                placeholder={getPlaceholderForInputText(field)}
                value={values[field.id]}
              />
            )}

            {field.type === "dropdown" && (
              <Select
                flex="1"
                value={values[field.id] || ""}
                onChange={ev => handleSelectChange(field, ev.target.value)}
                border={missingFieldIds.includes(field.id) ? "1px solid red" : ""}
                placeholder={field.placeholder || "Select an option"}
              >
                {(field.options || []).map((option: string) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </Select>
            )}

            {field.type === "borrowercontact" && (
              <Select
                flex="1"
                value={values[field.id] || ""}
                onChange={ev => setValue(field.id, ev.target.value)}
                isRequired={field.required}
                border={missingFieldIds.includes(field.id) ? "1px solid red" : ""}
              >
                <option value="">{field.placeholder || "Select borrower contact"}</option>
                {bcontacts.map((o: { label: string }) => (
                  <option key={o.label} value={o.label}>
                    {o.label}
                  </option>
                ))}
              </Select>
            )}

            {field.type === "property" && (
              <PropertySelect
                property={getProperty(field)}
                setProperty={property => handlePropertyChange(field, property)}
                companyId={loan?.borrower_company.id}
              />
            )}

            {field.type === "loancontact" && (
              <Select
                flex="1"
                data-cy={field.label}
                value={values[field.id] || ""}
                onChange={ev => setValue(field.id, ev.target.value)}
                border={missingFieldIds.includes(field.id) ? "1px solid red" : ""}
              >
                <option value="">{field.placeholder || "Select loan contact"}</option>
                {(loan?.contacts || []).map((contact: any, index: number) => (
                  <option
                    key={index}
                    value={`${contact.user.first_name} ${contact.user.last_name}`}
                  >
                    {`${contact.user.first_name} ${contact.user.last_name}`}
                  </option>
                ))}
              </Select>
            )}

            {field.type === "number" && (
              <Input
                data-cy={field.label}
                type="number"
                flex="1"
                value={(values[field.id] as string) || ""}
                onChange={ev => setValue(field.id, ev.target.value)}
                placeholder={getPlaceholderForInputText(field)}
                variant="primary"
              />
            )}

            {field.type === "dollar" && (
              <MoneyInput
                cy={field.label}
                value={(values[field.id] as string) || ""}
                onValueChange={val => setValue(field.id, val ?? "")}
              />
            )}

            {field.type === "percentage" && (
              <PercentInput
                cy={field.label}
                value={(values[field.id] as string) || ""}
                onValueChange={val => setValue(field.id, val ?? "")}
              />
            )}

            {field.type === "textarea" && (
              <Textarea
                data-cy={field.label}
                value={(values[field.id] as string) || ""}
                rows={4}
                flex="1"
                resize="none"
                onChange={ev => setValue(field.id, ev.target.value)}
                placeholder={field.placeholder || "Enter some details..."}
              />
            )}

            {field.type === "location" && (
              <LocationAutocomplete
                onChange={val => handleLocationChange(val, field.id)}
                defaultValue={
                  values[field.id]?.formatted || values[field.id] || loan?.location?.formatted
                }
              />
            )}

            {field.type === "radio" && (
              <RadioGroup
                flex="1"
                onChange={value => setValue(field.id, value)}
                value={values[field.id]}
              >
                <Stack>
                  {(field.options || []).map((option: string) => (
                    <Radio key={option} value={option}>
                      {option}
                    </Radio>
                  ))}
                </Stack>
              </RadioGroup>
            )}

            {field.type === "radioFloatingRate" && (
              <FloatingInterestRate
                onChange={result => {
                  setValue(field.id, result);
                }}
                isInvalid={missingFieldIds.includes(field.id)}
                field={field}
                value={getValue(field)}
                isDisabled={isFieldDisabled(user?.type, field)}
              />
            )}

            {field.type === "date" && (
              <DateInput
                data-cy={field.label}
                value={values[field.id] || ""}
                onChange={ev => setValue(field.id, ev.target.value)}
              />
            )}

            {field.type === "mask" && (
              <MaskInputs
                fields={field}
                onChange={formattedRegex => setValue(field.id, formattedRegex)}
                defaultValue={""}
              />
            )}

            {field.type === "email" && (
              <>
                <Input
                  type="email"
                  value={values[field.id] || ""}
                  onChange={ev => {
                    const email = ev.target.value.trim().toLowerCase();
                    setValue(field.id, email);
                  }}
                  placeholder="example@domain.com"
                  variant="primary"
                />
                {values[field.id] && !isValidEmail(values[field.id]) && (
                  <FormErrorMessage>Please enter a valid email address</FormErrorMessage>
                )}
              </>
            )}

            {field.type === "phone" && (
              <Input
                type="tel"
                value={values[field.id] || ""}
                onChange={ev => {
                  const input = ev.target.value.replace(/\D/g, "").slice(0, 10);
                  const formattedInput = formatPhoneNumber(input);
                  setValue(field.id, formattedInput);
                }}
                placeholder="(123) 456-7890"
                variant="primary"
              />
            )}

            {missingFieldIds.includes(field.id) && (
              <FormErrorMessage>This field is required</FormErrorMessage>
            )}
          </FormControl>
        ))}
    </SimpleGrid>
  );
};

export default LoanTypeFields;
